import React, { Dispatch, useEffect, useReducer } from 'react';
import { Skeleton, Stack, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import { CalendarMonth as CalendarMonthIcon } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApplicationAction, IApplication, ApplicationState } from '../types';
import Api from '../API';
import Tasks from './Tasks';
import { classes as tableStyles } from '../../../TableStyles';
import dayjs from 'dayjs';
import { ScoutCalendarReducer, InitialScoutCalendarState } from '../../../ScoutCalendar/reducer';
import CreateEvent from '../../../ScoutCalendar/CreateEvent/CreateEvent';
import ViewEvent from '../../../ScoutCalendar/ViewEvent';
import { IEvent } from 'app/javascript/components/ScoutCalendar/types';
import StyledSnackbar from '../../Components/CustomUIElements/StyledSnackbar';
import { ISnackbarInput } from '../../Components/CustomUIElements/StyledSnackbar';
import { styles } from '../styles';
import { ModalType } from '../config';
import { ActionButton } from '../OverviewTab/Actions';
import { formatUserPermissions } from '../../Components/Utilities/formatUserPermissions';

export default function TasksAndEventsTab({
  ApplicationState,
  dispatch,
  currentUserId
}: {
  ApplicationState: ApplicationState;
  dispatch: Dispatch<ApplicationAction>;
  currentUserId: number;
}) {
  const { userTimeZone } = ApplicationState;
  const queryClient = useQueryClient();
  const application = queryClient.getQueryData<IApplication>(['application']);
  const permissions = formatUserPermissions();

  const [ScoutCalendarState, eventDispatch] = useReducer(
    ScoutCalendarReducer,
    InitialScoutCalendarState
  );
  const { createEventObject } = ScoutCalendarState;

  const { data: events, isLoading: loadingEvents } = useQuery({
    queryKey: ['application events'],
    queryFn: async () => {
      if (application) {
        const { res } = await Api.getApplicationEvents(application.job.id, application.id);
        return res.events;
      }
    },
    onError: () =>
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: `There was an error in loading events`, state: 'error' }
      })
  });

  const headerStyle = (name: string) => {
    switch (name) {
      case 'Event':
        return {
          minWidth: '150px'
        };
      case 'Type':
        return { minWidth: '150px' };
      case 'Scheduled':
        return { minWidth: '120px' };
      case 'Event Owner':
        return { minWidth: '120px' };
      default:
        return { minWidth: '90px' };
    }
  };

  function handleSelectEvent(event: IEvent) {
    eventDispatch({
      type: 'SET_CREATE_EVENT_OBJECT',
      payload: {
        ...createEventObject,
        eventName: event.subject,
        eventDescription: event.note || '',
        maxCandidates: String(event.max_candidates),
        recruiters: event.attendees
          .filter((a) => a.type === 'User')
          .map((a) => ({
            id: a.id,
            name: a.name,
            checked: false,
            attendeeId: a.attendee_id,
            state: a.state
          })),
        eventType: event.event_type,
        duration: String(event.duration_in_minutes),
        eventDate: new Date(event.begins_at),
        eventId: event.id,
        candidates: event.attendees.filter((a) => a.type === 'Candidate'),
        dates: [new Date(event.begins_at)],
        owner: event.owner
      }
    });
    eventDispatch({ type: 'SET_SHOW_VIEW_EVENT_MODAL', payload: true });
  }

  const actions = [
    {
      label: 'Add to event',
      icon: <></>,
      onClick: () => dispatch({ type: 'SET_MODALS_OPEN', payload: ModalType.ADD_TO_EVENT }),
      permission: true
    },
    {
      label: 'Invite to timeslot',
      icon: <></>,
      onClick: () => dispatch({ type: 'SET_MODALS_OPEN', payload: ModalType.INVITE_TO_TIMESLOT }),
      permission: permissions?.Applications?.['Invite To Book In']
    }
  ];

  return (
    <Stack sx={{ rowGap: 3 }}>
      <Tasks ApplicationState={ApplicationState} dispatch={dispatch} />
      <Stack
        sx={{
          padding: 3,
          borderRadius: '6px',
          border: '1px solid #E3E3E3',
          rowGap: 2
        }}
      >
        <Stack
          sx={{
            fontSize: '18px',
            fontWeight: 600,
            color: '#333333',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Stack>Events</Stack>
          <Stack sx={{ flexDirection: 'row', columnGap: 2 }}>
            {actions
              .filter((a) => a.permission)
              .map((action, i) => (
                <ActionButton
                  key={i}
                  action={action}
                  boldFont={true}
                  buttonId={`${action.label.toLowerCase().split(' ').join('-')}-button`}
                />
              ))}
          </Stack>
        </Stack>
        {loadingEvents ? (
          [...Array(5)].map((_, index) => <Skeleton key={index} height={30} animation="wave" />)
        ) : (
          <>
            <Table stickyHeader sx={tableStyles.tableStyling}>
              <TableHead>
                <TableRow>
                  {['Event', 'Type', 'Scheduled', 'Event Owner'].map((title, index) => (
                    <TableCell
                      sx={{ ...tableStyles.tableHeader, ...headerStyle(title) }}
                      key={index}
                    >
                      <Stack>{title}</Stack>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {events?.length ? (
                  <>
                    {events?.map((e) => (
                      <TableRow
                        id="application-event-link"
                        key={e.id}
                        onClick={() => handleSelectEvent(e)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={{ fontWeight: 'bold', color: '#666666' }}>
                          {e.subject}
                        </TableCell>
                        <TableCell>{e.event_type.name}</TableCell>
                        <TableCell>
                          <Stack>
                            <Stack>{dayjs(e.begins_at).format('ddd, D MMM YYYY')}</Stack>
                            <Stack>
                              {dayjs(e.begins_at).format('LT')} - {dayjs(e.ends_at).format('LT')}
                            </Stack>
                          </Stack>
                        </TableCell>
                        <TableCell>{e.owner}</TableCell>
                      </TableRow>
                    ))}
                  </>
                ) : (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Stack sx={{ padding: 7.5, alignItems: 'center', rowGap: 2 }}>
                        <CalendarMonthIcon sx={styles.emptyStateIcon} />
                        <Stack sx={styles.emptyStateMessage}>No events added</Stack>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </>
        )}
      </Stack>
      {ScoutCalendarState.showCreateEventModal && events && (
        <CreateEvent
          events={events}
          ScoutCalendarState={ScoutCalendarState}
          dispatch={eventDispatch}
          userTimezone={userTimeZone}
        />
      )}
      {ScoutCalendarState.showViewEventModal && events && (
        <ViewEvent
          events={events}
          ScoutCalendarState={ScoutCalendarState}
          dispatch={eventDispatch}
          currentUserId={currentUserId}
          userTimezone={userTimeZone}
          newApplicationEnabled={true} // because to see this page, the new application must already be enabled
        />
      )}
      <StyledSnackbar
        message={ScoutCalendarState.snackbar.message}
        state={ScoutCalendarState.snackbar.state}
        setSnackbarState={(snackbarInput: ISnackbarInput) =>
          eventDispatch({ type: 'SET_SNACKBAR', payload: snackbarInput })
        }
      />
    </Stack>
  );
}
